import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import type {
  LatLng,
  LocationPermission,
  MapContextValue,
  ProjectionType,
  TracePoint,
  UserLocation,
} from '../types';
import logger from '../utils/logger';

import { useAuth } from './AuthContext';

/**
 * MapContext - Context for managing map and trace state
 *
 * Provides centralized state management for map-related functionality including:
 * - Map projection and view settings
 * - Zoom level and positioning
 * - Trace data and viewing state
 * - Map interaction modes
 * - Geolocation handling
 */
const MapContext = createContext<MapContextValue | null>(null);

/**
 * Props for MapProvider component
 */
interface MapProviderProps {
  children: ReactNode;
}

/**
 * Custom hook to access MapContext
 * @returns Map context value
 * @throws Error if used outside MapProvider
 */
export const useMap = (): MapContextValue => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMap must be used within a MapProvider');
  }
  return context;
};

/**
 * MapProvider - Provides map state to child components
 *
 * Manages all map-related state including projection settings,
 * zoom levels, trace data, and user location preferences.
 */
export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  const { projection: authProjection } = useAuth();

  // Use projection from localStorage (user's last choice) or AuthContext (from database)
  // Fallback to 'timespace' if neither has a valid value
  const [projection, setProjection] = useState<ProjectionType | string>(() => {
    const storedProjection = window.localStorage.getItem('projection');
    return storedProjection || authProjection || 'timespace';
  });

  // Zoom is intentionally NOT persisted: the viewport (center + zoom) is recomputed
  // fresh on every reload so refresh always re-centers deterministically rather than
  // restoring a stale, half-persisted position. See resetMapView/Map.tsx center priority.
  const [zoom, setZoom] = useState<number>(15);

  const [center, setCenter] = useState<LatLng | null>(null);
  const [waitForPos, setWaitForPos] = useState<boolean>(true);

  // Trace state
  const [currentTrace, setCurrentTrace] = useState<TracePoint[] | null>(null);
  const [isTraceViewOpen, setIsTraceViewOpen] = useState<boolean>(false);
  const [tracedDevice, setTracedDevice] = useState<string | null>(null);

  // One-shot fit-bounds request passed to TimeSpace when a geofence device is selected.
  const [fitBoundsRequest, setFitBoundsRequest] = useState<{
    swNe: [LatLng, LatLng];
    padding: number;
  } | null>(null);

  const requestFitBounds = (swNe: [LatLng, LatLng], padding: number): void => {
    setFitBoundsRequest({ swNe, padding });
  };

  const clearFitBoundsRequest = (): void => {
    setFitBoundsRequest(null);
  };

  // Map interaction state
  const [isMapInteractionEnabled, setIsMapInteractionEnabled] = useState<boolean>(true);
  const [showAccuracyCircles, setShowAccuracyCircles] = useState<boolean>(() => {
    const stored = window.localStorage.getItem('show-accuracy-circles');
    return stored !== null ? stored === 'true' : false;
  });

  // User location state
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationPermission, setLocationPermission] = useState<LocationPermission>('prompt');

  /**
   * Sync projection with AuthContext on mount only
   */
  useEffect(() => {
    if (authProjection && authProjection !== projection) {
      logger.debug(`MapContext: initial projection sync from ${projection} to ${authProjection}`);
      setProjection(authProjection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authProjection]);

  /**
   * Persist projection setting to localStorage
   */
  useEffect(() => {
    if (projection) {
      window.localStorage.setItem('projection', projection);
    }
  }, [projection]);

  /**
   * Persist accuracy circles setting
   */
  useEffect(() => {
    window.localStorage.setItem('show-accuracy-circles', showAccuracyCircles.toString());
  }, [showAccuracyCircles]);

  /**
   * Request user geolocation on mount
   */
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: UserLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          };

          setUserLocation(location);
          setLocationPermission('granted');
          setWaitForPos(false);

          // Set initial center if not already set
          if (!center) {
            setCenter([location.lat, location.lng]);
          }

          logger.debug('User location acquired:', location);
        },
        (error) => {
          logger.warn('Geolocation error:', error.message);
          setLocationPermission('denied');
          setWaitForPos(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    } else {
      logger.warn('Geolocation not supported by this browser');
      setLocationPermission('denied');
      setWaitForPos(false);
    }
  }, [center]);

  /**
   * Open trace view for a specific device
   */
  const openTraceView = (deviceId: string, traceData: TracePoint[] = []): void => {
    setTracedDevice(deviceId);
    setCurrentTrace(traceData);
    setIsTraceViewOpen(true);
    logger.debug(`Opening trace view for device: ${deviceId}`);
  };

  /**
   * Close trace view and return to main map
   */
  const closeTraceView = (): void => {
    setIsTraceViewOpen(false);
    setCurrentTrace(null);
    setTracedDevice(null);
    logger.debug('Closed trace view');
  };

  /**
   * Update trace data for the currently traced device
   */
  const updateTraceData = (newTraceData: TracePoint[]): void => {
    if (isTraceViewOpen && tracedDevice) {
      setCurrentTrace(newTraceData);
      logger.debug(`Updated trace data for device ${tracedDevice}: ${newTraceData.length} points`);
    }
  };

  /**
   * Center map on specific coordinates
   */
  const centerMapOn = (coordinates: LatLng): void => {
    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      setCenter([coordinates[0], coordinates[1]]);
      logger.debug(`Centered map on: ${coordinates[0]}, ${coordinates[1]}`);
    }
  };

  /**
   * Center map on user's current location
   */
  const centerMapOnUser = (): void => {
    if (userLocation) {
      setCenter([userLocation.lat, userLocation.lng]);
      logger.debug('Centered map on user location');
    } else {
      logger.warn('User location not available');
    }
  };

  /**
   * Toggle between map projection modes
   */
  const toggleProjection = (): void => {
    const newProjection = projection === 'timespace' ? 'sideways' : 'timespace';
    setProjection(newProjection);
    logger.debug(`Switched to ${newProjection} projection`);
  };

  /**
   * Reset map to default state
   */
  const resetMapView = (): void => {
    setZoom(15);
    setCenter(userLocation ? [userLocation.lat, userLocation.lng] : null);
    setProjection('timespace');
    logger.debug('Reset map view to defaults');
  };

  /**
   * Get current map bounds for device filtering
   * @returns null (not implemented)
   */
  const getMapBounds = (): null => {
    logger.debug('Map bounds requested (not implemented)');
    return null;
  };

  const value: MapContextValue = {
    // Map view state
    projection,
    setProjection,
    zoom,
    setZoom,
    center,
    setCenter,
    waitForPos,
    setWaitForPos,

    // Trace state
    currentTrace,
    isTraceViewOpen,
    tracedDevice,
    openTraceView,
    closeTraceView,
    updateTraceData,

    // Map interaction
    isMapInteractionEnabled,
    setIsMapInteractionEnabled,
    showAccuracyCircles,
    setShowAccuracyCircles,

    // User location
    userLocation,
    locationPermission,

    // Fit-bounds
    fitBoundsRequest,
    requestFitBounds,
    clearFitBoundsRequest,

    // Map actions
    centerMapOn,
    centerMapOnUser,
    toggleProjection,
    resetMapView,
    getMapBounds,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

export default MapProvider;
